// The requests behind the campaign screens, as hooks. Each keeps the last good
// answer on screen while the next one is on its way, and drops an answer that
// arrives after a newer one was asked for — a slow estimate must not overwrite
// the one the marketer just refreshed.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  HALT_CHANGED,
  fetchCampaignGroup,
  fetchCampaignGroupSummary,
  fetchGroupEstimate,
  fetchSendingIdentities,
  fetchSendingStatus,
  previewVersion,
} from "@/lib/api";
import type { RenderState } from "@/pages/campaigns/checks";
import type {
  CampaignGroupDetail,
  CampaignGroupSummary,
  CountryEstimate,
  SendingIdentity,
  SendingStatus,
} from "@/lib/api";
import { messageOf } from "@/pages/campaigns/model";

/** A counter that says whether an answer is still the newest one asked for. */
function useLatest() {
  const counter = useRef(0);
  return useCallback(() => {
    counter.current += 1;
    const ticket = counter.current;
    return () => ticket === counter.current;
  }, []);
}

// --- one group -------------------------------------------------------------------

export interface GroupData {
  detail: CampaignGroupDetail | null;
  summary: CampaignGroupSummary | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
}

export function useGroup(id: number | null): GroupData {
  const [detail, setDetail] = useState<CampaignGroupDetail | null>(null);
  const [summary, setSummary] = useState<CampaignGroupSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(id !== null);
  const latest = useLatest();

  const reload = useCallback(async () => {
    if (id === null) return;
    const current = latest();
    setLoading(true);
    try {
      const [nextDetail, nextSummary] = await Promise.all([
        fetchCampaignGroup(id),
        fetchCampaignGroupSummary(id),
      ]);
      if (!current()) return;
      setDetail(nextDetail);
      setSummary(nextSummary);
      setError(null);
    } catch (raised: unknown) {
      if (current()) setError(messageOf(raised));
    } finally {
      if (current()) setLoading(false);
    }
  }, [id, latest]);

  useEffect(() => {
    setDetail(null);
    setSummary(null);
    void reload();
  }, [reload]);

  return { detail, summary, error, loading, reload };
}

// --- whose name each country goes out under (rule 5, read-only) ----------------

let identitiesOnce: Promise<SendingIdentity[]> | null = null;

/** The sending accounts, asked for once per page load: they change when an
 * operator edits an account, not while a marketer builds a campaign. */
export function useSendingIdentities(): {
  identities: SendingIdentity[];
  error: string | null;
  identityOf: (country: string) => SendingIdentity | null;
} {
  const [identities, setIdentities] = useState<SendingIdentity[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    identitiesOnce ??= fetchSendingIdentities().catch((raised: unknown) => {
      identitiesOnce = null;
      throw raised;
    });
    identitiesOnce
      .then((rows) => {
        if (alive) setIdentities(rows);
      })
      .catch((raised: unknown) => {
        if (alive) setError(messageOf(raised));
      });
    return () => {
      alive = false;
    };
  }, []);

  const identityOf = useCallback(
    (country: string) => identities.find((row) => row.country === country) ?? null,
    [identities],
  );
  return { identities, error, identityOf };
}

// --- the estimate --------------------------------------------------------------------

export interface EstimateData {
  rows: CountryEstimate[] | null;
  /** When the numbers were counted — they age while the page is open. */
  at: Date | null;
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
}

/** Counting a country's audience reads its whole contact table, so the
 * estimate is asked for when a step needs it, not on every render. */
export function useEstimate(id: number | null): EstimateData {
  const [rows, setRows] = useState<CountryEstimate[] | null>(null);
  const [at, setAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useLatest();

  const load = useCallback(async () => {
    if (id === null) return;
    const current = latest();
    setLoading(true);
    try {
      const answer = await fetchGroupEstimate(id);
      if (!current()) return;
      setRows(answer);
      setAt(new Date());
      setError(null);
    } catch (raised: unknown) {
      if (current()) setError(messageOf(raised));
    } finally {
      if (current()) setLoading(false);
    }
  }, [id, latest]);

  return { rows, at, loading, error, load };
}

// --- every country's letter, as the sender renders it -----------------------------------

/** What one country's send renders: a letter version, and the wording laid
 * over it. A blank subject or preheader is the version's own. */
export interface RenderSource {
  versionId: number;
  subject?: string;
  preheader?: string;
}

/** One preview per country, in the country's language — the preview endpoint
 * picks the variant with the sender's own function, so the subject it answers
 * is the subject that goes out. `sourceOf` says what each country sends: the
 * group's letter, or a localised country's own letter and wording (4.3). A
 * country with no source yet gets no entry. Asked again only when a source,
 * the countries or their languages change. */
export function useRenders(
  sourceOf: (country: string) => RenderSource | null,
  countries: string[],
  languageOf: (country: string) => string | null,
): Record<string, RenderState> {
  const [renders, setRenders] = useState<Record<string, RenderState>>({});
  const wanted = countries
    .map((country) => [country, languageOf(country), sourceOf(country)] as const)
    .filter(
      (entry): entry is readonly [string, string, RenderSource] =>
        entry[1] !== null && entry[2] !== null,
    );
  const key = wanted
    .map(
      ([country, language, source]) =>
        `${country}:${language}:${source.versionId}:${source.subject ?? ""}:${source.preheader ?? ""}`,
    )
    .join("|");

  useEffect(() => {
    if (wanted.length === 0) {
      setRenders({});
      return undefined;
    }
    let alive = true;
    setRenders(Object.fromEntries(wanted.map(([country]) => [country, { status: "loading" }])));
    for (const [country, language, source] of wanted) {
      const versionId = source.versionId;
      previewVersion({
        version_id: versionId,
        language,
        country,
        ...(source.subject ? { subject: source.subject } : {}),
        ...(source.preheader ? { preheader: source.preheader } : {}),
      })
        .then((preview) => {
          if (alive) {
            setRenders((current) => ({ ...current, [country]: { status: "ok", preview, versionId } }));
          }
        })
        .catch((raised: unknown) => {
          if (alive) {
            setRenders((current) => ({
              ...current,
              [country]: { status: "error", message: messageOf(raised) },
            }));
          }
        });
    }
    return () => {
      alive = false;
    };
    // `key` is the content of `wanted`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return renders;
}

// --- the global halt (F1) -------------------------------------------------------------

const HALT_POLL_MS = 15_000;

export interface HaltData {
  status: SendingStatus | null;
  /** The status could not be read — which is not the same as "not halted". */
  error: string | null;
  reload: () => Promise<void>;
}

/** The same `/sending/status` the header's red bar reads, on the same beat. */
export function useHaltStatus(): HaltData {
  const [status, setStatus] = useState<SendingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setStatus(await fetchSendingStatus());
      setError(null);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    }
  }, []);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), HALT_POLL_MS);
    // A page that throws or lifts the halt says so (UI-6); read it at once.
    const now = () => void reload();
    window.addEventListener(HALT_CHANGED, now);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(HALT_CHANGED, now);
    };
  }, [reload]);

  return { status, error, reload };
}
